import { Skeleton } from "@tql/ui-components-react/tql/indicators";
import type { UIEvent } from "react";
import { useEffect, useMemo, useState } from "react";

import styles from "./mobile-shipment-details-report.module.css";
import type { EnrichedShipmentDetail } from "./shipment-detail-card.tsx";
import { ShipmentDetailCard } from "./shipment-detail-card.tsx";

type MobileShipmentDetailsReportProps = {
	readonly data: EnrichedShipmentDetail[];
	readonly isLoading: boolean;
	readonly isError: boolean;
	readonly showRate: boolean;
};

const LOADING_CARD_COUNT = 6;
const PAGE_SIZE = 25;
const SCROLL_THRESHOLD = 2;

const LoadingShipmentDetailCard = () => (
	<div className={styles.loadingCard} data-testid={"shipment-card-skeleton"}>
		{Array.from({ length: 3 }, (_, index) => (
			<div key={index} className={styles.loadingRow}>
				<Skeleton animation={{ type: "wave" }} shape={"text"} />
				<Skeleton animation={{ type: "wave" }} shape={"text"} />
			</div>
		))}
	</div>
);

export const MobileShipmentDetailsReport = ({
	data,
	isLoading,
	isError,
	showRate,
}: MobileShipmentDetailsReportProps) => {
	const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

	useEffect(() => {
		setVisibleCount(PAGE_SIZE);
	}, [data]);

	const visibleData = useMemo(
		() => data.slice(0, Math.min(visibleCount, data.length)),
		[data, visibleCount],
	);

	const handleListScroll = (event: UIEvent<HTMLDivElement>) => {
		if (visibleCount >= data.length) return;
		const target = event.currentTarget;
		const distanceFromBottom =
			target.scrollHeight - (target.scrollTop + target.clientHeight);
		if (distanceFromBottom <= SCROLL_THRESHOLD) {
			setVisibleCount((count) => Math.min(count + PAGE_SIZE, data.length));
		}
	};

	return (
		<div className={styles.wrapper}>
			<div
				aria-busy={isLoading}
				className={styles.listScrollRegion}
				data-testid={"shipment-details-scroll-region"}
				onScroll={handleListScroll}
			>
				{isError ? (
					<div className={styles.errorMessage}>
						Failed to load report data. Please try again.
					</div>
				) : null}
				{!isError && isLoading
					? Array.from({ length: LOADING_CARD_COUNT }, (_, index) => (
							<LoadingShipmentDetailCard key={`loading-${index}`} />
						))
					: null}
				{!isError && !isLoading && data.length === 0 ? (
					<div className={styles.emptyMessage}>
						No shipment details found for the selected date range.
					</div>
				) : null}
				{!isError && !isLoading
					? visibleData.map((item) => (
							<ShipmentDetailCard
								key={item.poNumber}
								item={item}
								showRate={showRate}
							/>
						))
					: null}
			</div>
		</div>
	);
};
